/**
 * The hidden machine block the "AI suggestions" button
 * (`AgentAssistButton.tsx`) appends to its message: what the user was doing
 * when they asked (the dialog/page title, the route and the current —
 * possibly unsaved — form values) and that they asked for suggestions
 * (`intent: "suggest"`), so the assistant can offer what it can do for
 * exactly that input. Hidden from the user the same way the
 * form view hides its own machine block (`views/agentFormSpec.js`'s
 * `stripFormAnswerBlock`/`AGENT_FORM_BLOCK_RE`) — see `AgentChatBox.tsx`.
 *
 * This module is translation-agnostic: the human-readable line is built by
 * the caller (via Tolgee) and just passed in as `text`.
 */

export type AgentContextKind = "dialog" | "page";

export type AgentContextPayload = {
  kind: AgentContextKind;
  title: string;
  path: string;
  values: Record<string, unknown>;
  /** Always "suggest": the button asks what the assistant can offer here, never for an action. */
  intent: "suggest";
};

const AGENT_CONTEXT_FENCE_LANG = "agent-context";
const AGENT_CONTEXT_BLOCK_RE = new RegExp(
  "\\n\\n```" + AGENT_CONTEXT_FENCE_LANG + "\\n([\\s\\S]*?)\\n```\\s*$"
);

/** The values object is trimmed to roughly this many JSON characters. */
const MAX_VALUES_JSON_LENGTH = 4000;

/**
 * Field-name fragments whose value must never leave the browser in this
 * block, matched anywhere in the key once its camelCase/snake_case/kebab-case
 * separators are removed (`api_key`, `apiKey` and `API-KEY` all become
 * `apikey`).
 */
const SECRET_KEY_FRAGMENT_RE =
  /password|passwort|passwd|token|secret|iban|apikey|accesskey|privatekey/;

/**
 * Short secret words only masked as a whole word of the key (`pin`, `userPin`,
 * `pin_code`, `cardCvc`) — as a bare fragment they would also hit ordinary
 * fields such as `shipping`, `mapping` or `spinner`.
 */
const SECRET_KEY_WORDS = new Set(["pin", "pincode", "cvc", "cvv", "cvv2"]);

/** The key's words, lower-cased: `cardCvc` → `card`, `cvc`; `pin_code` → `pin`, `code`. */
function keyWords(key: string): string[] {
  return key
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z\d]+/)
    .filter(word => word.length > 0);
}

/** Whether `key` names a value that looks like a credential or payment secret. */
function isSecretKey(key: string): boolean {
  const words = keyWords(key);
  if (SECRET_KEY_FRAGMENT_RE.test(words.join(""))) return true;
  return words.some(word => SECRET_KEY_WORDS.has(word));
}

function isFileLike(value: unknown): boolean {
  return (
    (typeof File !== "undefined" && value instanceof File) ||
    (typeof Blob !== "undefined" && value instanceof Blob)
  );
}

/** Drops file/blob values (they cannot be serialized) and masks secret-looking keys, recursively. */
function sanitizeValue(value: unknown): unknown {
  if (isFileLike(value)) return undefined;
  if (Array.isArray(value)) return value.map(sanitizeValue);
  if (value !== null && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, entryValue] of Object.entries(value)) {
      if (isFileLike(entryValue)) continue;
      result[key] = isSecretKey(key) ? "***" : sanitizeValue(entryValue);
    }
    return result;
  }
  return value;
}

/**
 * The current (possibly unsaved) form values, ready to hand to the
 * assistant: files/blobs dropped, obviously secret fields masked, and the
 * whole thing trimmed to roughly `MAX_VALUES_JSON_LENGTH` JSON characters by
 * dropping trailing keys — never by cutting the JSON itself, which would
 * leave the backend an unparsable block.
 */
export function sanitizeAgentContextValues(
  values: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!values) return {};
  const sanitized = sanitizeValue(values) as Record<string, unknown>;
  const keys = Object.keys(sanitized);
  while (
    keys.length > 0 &&
    JSON.stringify(sanitized).length > MAX_VALUES_JSON_LENGTH
  ) {
    const lastKey = keys.pop();
    if (lastKey !== undefined) delete sanitized[lastKey];
  }
  return sanitized;
}

/**
 * The full message the "AI suggestions" button sends: the caller's
 * already-localized human-readable request (`text`), then the machine
 * payload as a trailing fenced block (see the module doc for why).
 */
export function buildAgentContextMessage(payload: {
  kind: AgentContextKind;
  title: string;
  path: string;
  values?: Record<string, unknown>;
  /** The human-readable line shown to the user, already localized by the caller. */
  text: string;
}): string {
  const context: AgentContextPayload = {
    kind: payload.kind,
    title: payload.title,
    path: payload.path,
    values: sanitizeAgentContextValues(payload.values),
    intent: "suggest",
  };
  return `${payload.text}\n\n\`\`\`${AGENT_CONTEXT_FENCE_LANG}\n${JSON.stringify(context)}\n\`\`\``;
}

/** The message as shown to the user: the machine block removed. */
export function stripAgentContextBlock(text: string): string {
  return text.replace(AGENT_CONTEXT_BLOCK_RE, "").trimEnd();
}

/** The machine payload a message's trailing fenced block carries, or `null` when there is none (or it does not parse). */
export function extractAgentContextPayload(
  text: string
): AgentContextPayload | null {
  const match = text.match(AGENT_CONTEXT_BLOCK_RE);
  const json = match?.[1];
  if (!json) return null;
  try {
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
      return null;
    return parsed as AgentContextPayload;
  } catch {
    return null;
  }
}
